<script>
import NavBar from './components/NavBar.vue'
import { Analytics } from '@vercel/analytics/vue';
import { analytics } from './firebase';
import { track } from '@vercel/analytics';
import { logEvent } from 'firebase/analytics';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { onMounted, watch } from 'vue';
import { getSeoMetadata, updatePageMetadata } from './utils/seo.js';

export default {
  components: { NavBar },
  name: 'App',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { locale } = useI18n();

    // 初始化时检查URL路径中是否有语言设置
    onMounted(() => {
      // 提取URL路径中的语言代码
      const pathSegments = route.path.split('/').filter(Boolean);
      const firstSegment = pathSegments[0];
      const supportedLocales = ['en', 'zh', 'de', 'vi', 'th', 'id', 'ms'];

      // 如果URL中有语言路径，设置为当前语言
      if (firstSegment && supportedLocales.includes(firstSegment)) {
        locale.value = firstSegment;
        localStorage.setItem('locale', firstSegment);
      }

      // 记录页面访问事件
      logEvent(analytics, 'page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        language: locale.value
      });

      track('page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        language: locale.value
      });

      // 添加结构化数据
      addStructuredData(locale.value);

      // 初始化时更新SEO元数据
      updateSeoMetadata();
    });

    // 当语言变化时更新URL路径和SEO元数据
    watch(locale, (newLocale, oldLocale) => {
      // 获取当前路径中的语言部分（如果有）
      const pathSegments = route.path.split('/').filter(Boolean);
      const firstSegment = pathSegments[0];
      const supportedLocales = ['zh', 'de', 'vi', 'th', 'id', 'ms'];

      // 保存新语言设置到localStorage
      localStorage.setItem('locale', newLocale);

      // 构建新路径
      let newPath = '';

      if (newLocale === 'en') {
        // 英语是默认语言，移除语言前缀
        if (supportedLocales.includes(firstSegment)) {
          // 当前路径有语言前缀，需要移除
          newPath = '/' + pathSegments.slice(1).join('/');
        } else {
          // 当前路径没有语言前缀，无需修改
          newPath = route.path;
        }
      } else {
        // 非英语语言，添加或替换语言前缀
        if (supportedLocales.includes(firstSegment)) {
          // 替换当前语言前缀
          newPath = '/' + newLocale + '/' + pathSegments.slice(1).join('/');
        } else {
          // 添加语言前缀
          newPath = '/' + newLocale + route.path;
        }
      }

      // 如果路径发生变化，使用replace导航以避免在历史记录中创建新条目
      if (newPath !== route.path) {
        router.replace(newPath);
      }

      // 更新SEO元数据
      updateSeoMetadata();
    });

    // 更新SEO元数据
    const updateSeoMetadata = () => {
      const pageName = route.meta.pageName || 'home';
      const seoMetadata = getSeoMetadata(locale.value, pageName);
      updatePageMetadata(seoMetadata);
    };

    // 添加结构化数据
    const addStructuredData = (locale) => {
      // 获取当前语言的基本SEO数据
      const seoData = getSeoMetadata(locale, 'home');

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": seoData.siteName,
        "description": seoData.description,
        "applicationCategory": "MultimediaApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "screenshot": [
          "https://uglyavatar.appsapp.org/examples/example-1.png",
          "https://uglyavatar.appsapp.org/examples/example-2.png"
        ],
        "featureList": "Random avatar generation,Save to collection,Customize hair and background,Download and share",
        "inLanguage": locale
      };

      // 添加结构化数据到页面
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.textContent = JSON.stringify(structuredData);
      } else {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(structuredData);
        document.head.appendChild(script);
      }
    }

    return { locale };
  }
}
</script>

<template>
  <Analytics />
  <div class="app-container">
    <NavBar />
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
  </div>
</template>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  /* 全局颜色变量 */
  --primary-color: #42b983;
  --primary-hover: #3aa876;
  --secondary-color: #2c3e50;
  --accent-color: #e74c3c;
  --light-bg: #f8f9fa;
  --dark-bg: #343a40;
  --text-primary: #333;
  --text-secondary: #666;
  --text-light: #fff;
  --border-radius-sm: 4px;
  --border-radius-md: 8px;
  --border-radius-lg: 16px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  --transition: all 0.2s ease;
}

body {
  font-family: 'Inter', 'Segoe UI', 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--light-bg);
  color: var(--text-primary);
  line-height: 1.6;
}

.app-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card {
  background: white;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: all 0.2s ease;
}

button {
  cursor: pointer;
  transition: var(--transition);
  border: none;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
}

button:focus {
  outline: none;
}

a {
  text-decoration: none;
  color: var(--primary-color);
  transition: var(--transition);
}

a:hover {
  color: var(--primary-hover);
}

.container {
  width: 100%;
  max-width: 1200px;
  padding: 0 1.5rem;
  margin: 0 auto;
}

.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  /* 统一圆角大小 */
  font-weight: 600;
  text-align: center;
  transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
  /* 更平滑的过渡效果 */
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.25);
}

.btn-primary {
  background-color: var(--primary-color);
  color: var(--text-light);
}

.btn-primary:hover {
  background-color: var(--primary-hover);
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 10px rgba(66, 185, 131, 0.3);
  color: var(--text-light);
}

.btn-primary:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 2px 4px rgba(66, 185, 131, 0.2);
}

.btn-secondary {
  background-color: #fff;
  color: var(--text-primary);
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #f5f5f5;
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: var(--text-primary);
}

.btn-secondary:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-danger {
  background-color: var(--accent-color);
  color: var(--text-light);
}

.btn-danger:hover {
  background-color: #d44335;
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 10px rgba(231, 76, 60, 0.3);
  color: var(--text-light);
}

.btn-danger:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 2px 4px rgba(231, 76, 60, 0.2);
}

/* 按钮内图标统一样式 */
.btn svg,
.btn .btn-icon {
  vertical-align: middle;
  margin-right: 0.5rem;
  display: inline-block;
}

/* 带有图标的按钮内边距调整 */
.btn.has-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

/* 较小的按钮变体 */
.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}

/* 较大的按钮变体 */
.btn-lg {
  padding: 0.9rem 1.8rem;
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }

  .btn {
    padding: 0.7rem 1.3rem;
    font-size: 0.9rem;
  }

  .btn-sm {
    padding: 0.45rem 0.9rem;
    font-size: 0.8rem;
  }

  .btn-lg {
    padding: 0.85rem 1.6rem;
    font-size: 1rem;
  }
}

/* 添加全局动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}
</style>
